import { useCallback, useEffect, useState } from 'react';
import { useAlert } from '../Components/AlertContext';
import Button from '../Components/Buttons';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import { CustomInput, CustomToggle } from '../Components/CustomFormElements';
import PageLoader from '../Components/PageLoader';
import SureLoader from '../Components/SureLoader';
import UserPhotoInput from '../Components/UserPhotoInput';
import { useAuth } from '../Contexts/AuthContext';
import { UsersApiProvider, useUsersApi } from '../Services/UsersApiProvider';
import { UserUpdateApi } from '../Services/UsersApiProviderTypes';
import './LoginPage.css';

const PASSWORD_REGEX = /^(?=(?:.*[!@#$%^&*()_+{}[\]:;<>,.?~]){2,})(?=(?:.*\d){2,}).{10,}$/;

interface ValidationRule {
  required?: boolean;
  regex?: RegExp;
  errorMessage: string;
}

type ValidationRules = Record<string, ValidationRule>;

function validateFields(
  fieldIds: string[],
  rules: ValidationRules,
  showAlert: (type: string, message: string) => void,
): boolean {
  let isValid = true;

  for (const fieldId of fieldIds) {
    const field = document.getElementById(fieldId) as HTMLInputElement | null;
    const rule = rules[fieldId];

    if (!field || !rule) {
      console.warn(`Field with ID '${fieldId}' not found.`);
      continue;
    }

    const value = field.value?.trim() || '';

    field.addEventListener('focus', () => field.classList.remove('input-error'), { once: true });

    if (rule.required && !value) {
      field.classList.add('input-error');
      showAlert('fail', rule.errorMessage);
      isValid = false;
      continue;
    }

    if (rule.regex && !rule.regex.test(value)) {
      field.classList.add('input-error');
      showAlert('fail', rule.errorMessage);
      isValid = false;
      continue;
    }

    field.classList.remove('input-error');
  }

  return isValid;
}

// const validateAccountFields = async (showAlert, setLoading) => {
//   const accountValidationRules = {
//     name: {
//       required: true,
//       regex: /^[^\s!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?0-9]+$/,
//       errorMessage: 'Enter a valid name. No spaces or special characters allowed.',
//     },
//     'last-name': {
//       required: true,
//       regex: /^[^\s!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?0-9]+$/,
//       errorMessage: 'Enter a valid last name. No spaces or special characters allowed.',
//     },
//     email: {
//       required: true,
//       regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
//       errorMessage: 'Enter a valid email address.',
//     },
//     company: {
//       required: true,
//       regex:
//         /^[^\s!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?0-9]+(?: [^\s!@#$%^&*()_+=\-[\]{};':"\\|,.<>/?0-9]+)*$/,
//       errorMessage: 'Enter a valid company name. No special characters allowed.',
//     },
//   };

//   const isValid = validateFields(
//     ['name', 'last-name', 'email', 'company'],
//     accountValidationRules,
//     showAlert,
//   );

//   if (!isValid) {
//     return false;
//   }

//   try {
//     setLoading(true); // Show loader
//     await new Promise((resolve) => setTimeout(resolve, 2000)); // Simulate API delay
//     showAlert('success', 'Account updated successfully!');
//     return true;
//   } catch (error) {
//     showAlert('fail', 'Failed to update account. Please try again.');
//     return false;
//   } finally {
//     setLoading(false); // Hide loader
//   }
// };

const PASSWORD_VALIDATION_RULES: ValidationRules = {
  password1: {
    required: true,
    regex: PASSWORD_REGEX,
    errorMessage: 'Password must have at least 10 characters, 2 special symbols, and 2 numbers.',
  },
  password2: {
    required: true,
    errorMessage: 'Confirm your password.',
  },
};

type ProfileForm = {
  name: string;
  notifySystem: boolean;
  photo: string;
};

const errorReason = (error): string =>
  error?.response?.data?.error || error?.response?.statusText || 'error';

const SuperAdminPageContent = () => {
  const { showAlert } = useAlert();
  const { changeUserPassword, getUserById, updateUser, syncUserPhoto } = useUsersApi();
  const { status, refreshStatus } = useAuth();
  const userId: string | undefined = status?.id;

  const [loading, setLoading] = useState<boolean>(false);
  const [password1, setPassword1] = useState<string>('');
  const [password2, setPassword2] = useState<string>('');
  const [profile, setProfile] = useState<ProfileForm | null>(null);
  const [savedProfile, setSavedProfile] = useState<ProfileForm | null>(null);
  const [profileLoadFailed, setProfileLoadFailed] = useState<boolean>(false);

  const loadProfile = useCallback(async () => {
    const data = await getUserById(userId);
    const loaded: ProfileForm = {
      name: data.name ?? '',
      notifySystem: Boolean(data.notify_system),
      photo: data.photo_url ?? '',
    };
    setProfile(loaded);
    setSavedProfile(loaded);
  }, [getUserById, userId]);

  useEffect(() => {
    if (!userId) return;
    loadProfile().catch((error) => {
      console.error('Failed to load profile', error);
      setProfileLoadFailed(true);
      showAlert('fail', 'Failed to load profile.');
    });
  }, [userId, loadProfile, showAlert]);

  const isProfileDirty =
    !!profile &&
    !!savedProfile &&
    (profile.name !== savedProfile.name ||
      profile.notifySystem !== savedProfile.notifySystem ||
      profile.photo !== savedProfile.photo);

  const updateProfile = (changes: Partial<ProfileForm>) =>
    setProfile((prev) => (prev ? { ...prev, ...changes } : prev));

  const handleSaveProfile = async () => {
    if (!profile || !savedProfile) return;

    const name = profile.name.trim();
    if (!name) {
      showAlert('fail', 'Name is required.');
      return;
    }

    const changes: Partial<UserUpdateApi> = {};
    if (name !== savedProfile.name) changes.name = name;
    if (profile.notifySystem !== savedProfile.notifySystem) {
      changes.notify_system = profile.notifySystem;
    }

    setLoading(true);
    try {
      if (Object.keys(changes).length) await updateUser(userId, changes);

      try {
        const photoChanged = await syncUserPhoto(userId, profile.photo, savedProfile.photo);
        if (photoChanged) refreshStatus?.();
      } catch (error) {
        setSavedProfile((prev) => ({ ...prev, name, notifySystem: profile.notifySystem }));
        updateProfile({ name });
        showAlert('fail', `Profile saved, but photo update failed: ${errorReason(error)}`);
        return;
      }

      try {
        await loadProfile();
      } catch (error) {
        console.error('Failed to reload profile', error);
      }
      showAlert('success', 'Profile updated successfully!');
    } catch (error) {
      console.error('Error saving profile', error);
      showAlert('fail', `Failed to update profile: ${errorReason(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    const isValid = validateFields(
      ['password1', 'password2'],
      PASSWORD_VALIDATION_RULES,
      showAlert,
    );

    if (!isValid) return;

    if (password1 !== password2) {
      showAlert('fail', 'Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await changeUserPassword(status.id, password1);
      showAlert('success', 'Password updated successfully!');
      setPassword1('');
      setPassword2('');
    } catch {
      showAlert('fail', 'Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      {loading && <SureLoader fullscreen />}
      <div className="row gap">
        <div className="d-flex radius-yes p-2 bg-yes col-12 flex-column gap">
          <div className="int-col-4 p-2">
            <h1 className="h1-title">Account</h1>
          </div>

          <div className="int-col-4 gap d-flex flex-row-to-col">
            {/* <div className="int-col-2 p-2 border-yes radius-yes">
              <div className="int-col-4 p-2">
                <h3 className="h3-title">General data</h3>
              </div>
              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Name</div>
                  <div className="small-font">Enter superadmin name</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="name"
                    label="Name"
                    placeholder="Enter name"
                    icon={<i className="fa-solid fa-user"></i>}
                    defaultValue=""
                    type="text"
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Last Name</div>
                  <div className="small-font">Enter superadmin last name </div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="last-name"
                    label="Last name"
                    placeholder="Enter last name"
                    icon={<i className="fa-solid fa-user"></i>}
                    defaultValue=""
                    type="text"
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Email</div>
                  <div className="small-font">Enter superadmin email </div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="email"
                    label="Email"
                    placeholder="Enter email"
                    icon={<i className="fa-solid fa-envelope"></i>}
                    type="text"
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Profile image</div>
                  <div className="small-font">Upload profile image</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <ConfirmationProvider>
                    <CustomFileUpload
                      multiple={false}
                      maxFileSize={1024 * 1024} // 2 MB
                      allowedFormats={['image/png', 'image/jpeg']}
                      showUploadedFiles={true}
                      allowFileDeletion={true}
                    />
                  </ConfirmationProvider>
                </div>
              </div>

              <div className="buttons-block w-100 justify-end d-flex flex-row flex-row-to-col">
                <Button background="color2" size="large" onClick={handleAccountUpdate}>
                  Update account
                </Button>
              </div>
            </div> */}
            <div className="int-col-2 p-2 border-yes radius-yes">
              <div className="int-col-4 p-2">
                <h2 className="h3-title">Profile</h2>
              </div>

              {profile ? (
                <>
                  <div className="input-block">
                    <UserPhotoInput
                      value={profile.photo}
                      onChange={(photo) => updateProfile({ photo })}
                      organizationId={status?.organization_id}
                    />
                  </div>

                  <div className="input-block">
                    <CustomInput
                      id="name"
                      label="Name"
                      value={profile.name}
                      placeholder="Enter name"
                      onChange={(_, val) => updateProfile({ name: val })}
                    />
                  </div>

                  <div className="input-row input-block d-flex flex-row justify-between">
                    <div className="input-description">
                      <div className="input-title">System Notifications</div>
                      <div className="small-font">
                        Enable or disable receiving system notifications
                      </div>
                    </div>
                    <div className="align-wrapper d-flex align-center">
                      <CustomToggle
                        id="system-notifications-toggle"
                        label={profile.notifySystem ? 'Enabled' : 'Disabled'}
                        checked={profile.notifySystem}
                        onChange={(_, value) => updateProfile({ notifySystem: value })}
                      />
                    </div>
                  </div>

                  <div className="buttons-block w-100 justify-end d-flex flex-row flex-row-to-col">
                    <Button
                      background="color2"
                      size="large"
                      onClick={handleSaveProfile}
                      disabled={!isProfileDirty || loading}
                    >
                      Save changes
                    </Button>
                  </div>
                </>
              ) : profileLoadFailed ? (
                <div className="small-font">Failed to load profile.</div>
              ) : (
                <PageLoader blockHeights={[136, 64, 64]} />
              )}
            </div>

            <div className="int-col-2 p-2 border-yes radius-yes">
              <div className="int-col-4 p-2">
                <h2 className="h3-title">Password</h2>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Enter password</div>
                  <div className="small-font">
                    Password shold have:
                    <ul>
                      <li>min 10 characters</li>
                      <li>min 2 symbols</li>
                      <li>min 2 numbers</li>
                    </ul>
                  </div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="password1"
                    label="Password"
                    placeholder="Enter Password"
                    toggleVisibility={true}
                    type="password"
                    value={password1}
                    onChange={(_, val) => setPassword1(val)}
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Confirm password</div>
                  <div className="small-font">Enter the same password as above</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="password2"
                    label="Confirm Password"
                    placeholder="Confirm password"
                    defaultValue=""
                    toggleVisibility={true}
                    type="password"
                    value={password2}
                    onChange={(_, val) => setPassword2(val)}
                  />
                </div>
              </div>

              <div className="buttons-block w-100 justify-end d-flex flex-row flex-row-to-col">
                <Button background="color2" size="large" onClick={handleChangePassword}>
                  Change password
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const UserAccount = () => {
  const { status } = useAuth();

  return (
    <ConfirmationProvider>
      <UsersApiProvider organizationId={status?.organization_id}>
        <SuperAdminPageContent />
      </UsersApiProvider>
    </ConfirmationProvider>
  );
};

export default UserAccount;
